import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faShip, faMapMarkerAlt, faDollarSign, faBoxes, faHandshake, faPercent, faClock, faTruck, faBuilding, faUser, faAnchor } from '@fortawesome/free-solid-svg-icons';

/**
 * Custom node component for document relationship visualization
 * Displays comprehensive document information in a colored card
 */
const InvoiceNode = ({ data, selected, history }) => {
  // Navigation handler
  const handleNodeClick = (e) => {
    e.stopPropagation();
    
    if (!history) return;
    
    // Navigate based on node type
    switch (data.type) {
      case 'DEAL':
        if (data.dealId && data.transactionType && data.dealType) {
          // Format: /offer-generator?offerId=${id}&type=${templateType}&dealType=${transactionType}
          const templateType = data.transactionType === 'Offer' ? 'offer' : 'bid';
          history.push(`/offer-generator?offerId=${data.dealId}&type=${templateType}&dealType=${data.dealType}`);
        }
        break;
      
      case 'RECAP':
        if (data.recapId) {
          history.push(`/trade-recap/${data.recapId}`);
        }
        break;

      case 'FREIGHT_RECAP':
        if (data.recapId) {
          history.push(`/freight-recap/${data.recapId}`);
        }
        break;
      
      case 'DELIVERY':
        if (data.deliveryId) {
          // Format: /deliveries/${deliveryId}
          history.push(`/deliveries/${data.deliveryId}`);
        }
        break;
      
      case 'PRODUCT':
        // Navigate to stock/risk analytics dashboard
        if (data.variantId) {
          // Pass variant ID as query parameter
          history.push(`/dashboard/risk-analytics?variantId=${data.variantId}`);
        } else {
          // Just go to the dashboard
          history.push('/dashboard/risk-analytics');
        }
        break;
      
      default:
        // For invoice types, do nothing for now
        break;
    }
  };
  const getTypeLabel = (type) => {
    const labels = {
      'PROFORMA': 'Proforma Invoice',
      'OUTGOING': 'Outgoing Invoice',
      'CREDIT_NOTE': 'Credit Note',
      'PURCHASE_ORDER': 'Purchase Order',
      'INCOMING': 'Incoming Invoice',
      'DEBIT_NOTE': 'Debit Note',
      'DEAL': 'Deal',
      'RECAP': 'Trade Recap',
      'FREIGHT_RECAP': 'Freight Recap',
      'DELIVERY': 'Delivery',
      'PRODUCT': 'Product'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': '#f59e0b',
      'PENDING_APPROVAL': '#f59e0b',
      'REJECTED': '#dc2626',
      'APPROVED': '#10b981',
      'SENT': '#3b82f6',
      'PAID': '#4a6b25',
      'CANCELLED': '#6b7280',
      // Trade operations statuses
      'Draft': '#f59e0b',
      'Generated': '#3b82f6',
      'Saved': '#10b981',
      'Dormant': '#d97706',
      'ACTIVE': '#10b981',
      'DELIVERY': '#8b5cf6',
      // Product statuses
      'COMMODITY': '#ffc20c',
      'LEGACY': '#8b5cf6',
      'STEAM_COAL': '#ffc20c',
      'IRON_ORE': '#ffc20c',
      'COKING_COAL': '#ffc20c',
      'ANTHRACITE': '#ffc20c',
      'PETCOKE': '#ffc20c',
      'CEMENT': '#ffc20c',
      'CHROME_ORE': '#ffc20c'
    };
    return colors[status] || '#9ca3af';
  };

  const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Darker shade for border
  const darkerColor = (color) => {
    const hex = color.replace('#', '');
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - 40);
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - 40);
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - 40);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Render field with icon
  const renderField = (icon, value, small = false, forceShow = false) => {
    // Allow showing values that are 0 if forceShow is true (for prices)
    if (!forceShow && (!value || value === 'N/A' || value === 'null' || value === 'undefined')) return null;
    if (!value || value === 'N/A' || value === 'null' || value === 'undefined') return null;
    return (
      <div style={{ 
        fontSize: small ? '10px' : '11px',
        marginBottom: '3px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        opacity: 0.95,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
      }}>
        {icon && <FontAwesomeIcon icon={icon} style={{ fontSize: '9px', opacity: 0.8 }} />}
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      </div>
    );
  };

  // Render content based on document type
  const renderContent = () => {
    switch (data.type) {
      case 'PRODUCT':
        return (
          <>
            {renderField(null, data.productType)}
            {renderField(faMapMarkerAlt, data.origin, true)}
            {data.variant && data.variant !== 'N/A' && renderField(null, `Variant: ${data.variant}`, true)}
            {renderField(faBoxes, data.quantity, true)}
            {data.purchasePrice && data.purchasePrice !== 'N/A' && renderField(faDollarSign, `Buy: ${data.purchasePrice}`, true, true)}
            {data.salePrice && data.salePrice !== 'N/A' && renderField(faDollarSign, `Sell: ${data.salePrice}`, true, true)}
            {renderField(faHandshake, data.supplier, true)}
          </>
        );
      
      case 'DEAL':
        return (
          <>
            {renderField(null, data.transactionType)}
            {renderField(null, `${data.product} (${data.origin})`)}
            {renderField(faBoxes, `${data.quantity} MT`)}
            {data.tolerance && data.tolerance !== 'N/A' && renderField(faPercent, `±${data.tolerance}%`, true)}
            {renderField(faDollarSign, `${data.currency} ${data.price}/MT`)}
            {renderField(faHandshake, data.counterparty, true)}
            {renderField(faTruck, data.incoterm, true)}
            {data.validity && data.validity !== 'N/A' && renderField(faClock, data.validity, true)}
          </>
        );
      
      case 'RECAP':
        // Format ports to only show available port, not "→ N/A"
        let portsDisplay = data.ports;
        if (portsDisplay && portsDisplay.includes('→ N/A')) {
          portsDisplay = portsDisplay.replace(' → N/A', '');
        } else if (portsDisplay && portsDisplay.includes('N/A →')) {
          portsDisplay = portsDisplay.replace('N/A → ', '');
        }
        
        return (
          <>
            {renderField(null, data.recapType)}
            {renderField(faShip, data.vessel)}
            {renderField(null, data.product, true)}
            {/* Quantity with Partial badge if applicable */}
            {data.quantity && data.quantity !== 'N/A' && (
              <div style={{ 
                fontSize: '11px',
                marginBottom: '3px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: 0.95,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>
                <FontAwesomeIcon icon={faBoxes} style={{ fontSize: '9px', opacity: 0.8 }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.quantity}</span>
                {data.isPartialAllocation && (
                  <span style={{
                    backgroundColor: '#9b59b6',
                    color: 'white',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: '600',
                    marginLeft: '4px'
                  }}>
                    Partial
                  </span>
                )}
              </div>
            )}
            {renderField(faDollarSign, data.contractPrice, true)}
            {renderField(faHandshake, data.counterparty, true)}
            {renderField(faMapMarkerAlt, portsDisplay, true)}
            {data.commission && data.commission !== 'N/A' && renderField(null, `Comm: ${data.commission}`, true)}
          </>
        );
      
      case 'FREIGHT_RECAP': {
        let freightPortsDisplay = data.ports;
        if (freightPortsDisplay && freightPortsDisplay.includes('\u2192 N/A')) {
          freightPortsDisplay = freightPortsDisplay.replace(' \u2192 N/A', '');
        } else if (freightPortsDisplay && freightPortsDisplay.includes('N/A \u2192')) {
          freightPortsDisplay = freightPortsDisplay.replace('N/A \u2192 ', '');
        }

        return (
          <>
            {renderField(faShip, data.vessel)}
            {renderField(null, data.product, true)}
            {renderField(faBoxes, data.quantity, true)}
            {renderField(faDollarSign, data.freightPrice, true)}
            {renderField(faAnchor, data.charterer, true)}
            {renderField(faHandshake, data.owners, true)}
            {renderField(faMapMarkerAlt, freightPortsDisplay, true)}
            {renderField(faCalendar, data.laycan, true)}
          </>
        );
      }

      case 'DELIVERY':
        // Format ports to only show available port, not "→ N/A"
        let deliveryPortsDisplay = data.ports;
        if (deliveryPortsDisplay && deliveryPortsDisplay.includes('→ N/A')) {
          deliveryPortsDisplay = deliveryPortsDisplay.replace(' → N/A', '');
        } else if (deliveryPortsDisplay && deliveryPortsDisplay.includes('N/A →')) {
          deliveryPortsDisplay = deliveryPortsDisplay.replace('N/A → ', '');
        }
        
        return (
          <>
            {renderField(faShip, `${data.vessel} (${data.imo})`)}
            {renderField(null, data.product, true)}
            {renderField(faBoxes, data.blQuantity, true)}
            {renderField(faMapMarkerAlt, deliveryPortsDisplay, true)}
            {renderField(faUser, data.buyer, true)}
            {renderField(faHandshake, data.seller, true)}
            {renderField(faBuilding, data.hmsEntity, true)}
          </>
        );
      
      default:
        // Original invoice display logic
        return (
          <>
            {/* Amount or Info */}
            <div style={{ 
              fontSize: data.info ? '12px' : '15px', 
              fontWeight: 'bold',
              marginBottom: '4px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}>
              {data.info || `${data.currency} ${formatAmount(data.amount)}`}
            </div>

            {/* Date */}
            {data.date && (
              <div style={{ 
                fontSize: '11px',
                opacity: 0.9,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>
                <FontAwesomeIcon icon={faCalendar} />
                <span>{formatDate(data.date)}</span>
              </div>
            )}
          </>
        );
    }
  };

  return (
    <div
      onClick={handleNodeClick}
      style={{
        backgroundColor: data.color,
        border: `${selected || data.isSelected ? '3px' : '2px'} solid ${darkerColor(data.color)}`,
        borderRadius: '8px',
        padding: '10px 12px',
        minWidth: '200px',
        maxWidth: '300px',
        boxShadow: selected || data.isSelected
          ? `0 4px 20px ${data.color}80`
          : '0 2px 8px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
      className="invoice-node"
    >
      {/* Handles for connections */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: darkerColor(data.color), width: 8, height: 8 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: darkerColor(data.color), width: 8, height: 8 }}
      />

      {/* Node content */}
      <div style={{ color: '#fff' }}>
        {/* Type label */}
        <div style={{ 
          fontSize: '13px', 
          fontWeight: 'bold', 
          marginBottom: '4px',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
        }}>
          {getTypeLabel(data.type)}
        </div>

        {/* Document number */}
        <div style={{ 
          fontSize: '12px', 
          marginBottom: '6px',
          fontWeight: '600',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
        }}>
          {data.number}
        </div>

        {/* Divider */}
        <div style={{ 
          borderTop: `1px solid ${darkerColor(data.color)}`, 
          margin: '6px 0' 
        }} />

        {/* Status badge */}
        <div style={{ 
          display: 'inline-block',
          backgroundColor: '#fff',
          color: '#4a6b25',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '600',
          marginBottom: '6px',
          border: '1px solid #4a6b25'
        }}>
          {data.status}
        </div>

        {/* Type-specific content */}
        <div style={{ marginTop: '4px' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default memo(InvoiceNode);
